import {
  ApiError,
  disconnectXeroConnection,
  startXeroReconnect,
  fetchDeadlines,
  createMeeting,
  fetchDetail,
  fetchMe,
  fetchMeetings,
  fetchView,
  fetchXeroConnections,
  forgetCsrfToken,
  refreshClient,
  sendFeedback,
  signOut,
  rememberCsrfFrom,
  refreshCsrfFrom,
  useLoginBase,
} from "./api.js";
import type { DeadlinesPayload, DetailPayload, FindingItem, Me, MeetingsList, ViewPayload, XeroConnectionRow } from "./api.js";
import { showSignIn, showTerms } from "./auth.js";
import type { TermsRequired } from "./terms-copy.js";
import { VIDEO_AND_SHARE, viewerTimeZone } from "./meetings.js";
import { mountGuest, mountRoom, mountSummary, openNewMeetingDialog, renderMeetingsPage } from "./meetings-ui.js";
import { renderClientPage, renderClientsPage } from "./clients-ui.js";
import { mountRecorderStart } from "./recorder-start.js";
import { confirmDialog, startLiveRecording } from "./recorder-live.js";
import type { LiveRecording } from "./recorder-live.js";
import { discardUnsent, loadUnsent, renderUnsentBanners, uploadUnsent } from "./recorder-banner.js";
import type { RecoverableRecording } from "./recorder/types.js";
import { DISCARD_FAILED, isSignedOut, SIGNED_OUT, signOutCopy } from "./recorder.js";
import type { Mounted } from "./meetings-ui.js";
import {
  ONBOARDING,
  buildUrl,
  isMeetingsView,
  pageTitle,
  parseRoute,
  sanitizeClients,
  viewKey,
  visibleView as visibleViewFor,
} from "./nav.js";
import type { FilterOption, View } from "./nav.js";
import {
  createClientFilter,
  createFeedbackPanel,
  renderConnections,
  renderDeadlines,
  renderDetail,
  renderDisconnectPanel,
  renderReconnectPanel,
  renderFollowUp,
  renderNotFound,
  renderRail,
  renderRailFooter,
  renderSettings,
  renderSoonPage,
  renderSummaryStrip,
  renderTopBar,
} from "./render.js";
import type { DisconnectPanelState, FeedbackPanelState, RailOptions, ReconnectPanelState } from "./render.js";
import { ADMIN_COPY, adminSignOut, initRecorderHost, inviteToken, showInviteAccept, startV05 } from "./v05/index.js";
import { useV05 } from "./v05/routes.js";

const appEl = document.getElementById("pk-app")!;
const authEl = document.getElementById("pk-auth")!;
const railEl = document.getElementById("pk-rail")!;
const railNavEl = document.getElementById("pk-rail-nav")!;
const railFooterEl = document.getElementById("pk-rail-footer")!;
const backdropEl = document.getElementById("pk-backdrop")!;
const mainEl = document.getElementById("pk-main")!;
const contentEl = document.getElementById("pk-content")!;
const detailEl = document.getElementById("pk-detail")!;
// Full screen, outside the console's rail: the meeting room and a guest's lobby.
const meetingEl = document.getElementById("pk-meeting")!;
// Console v0.5 (design brief v0.5): its own root, used only when /api/me says console_v05.
const v05El = document.getElementById("pk5-app")!;
let v05Started = false;

// What the side panel is showing: the selected finding's detail (the
// default, Follow-up only), the feedback form, or a disconnect confirmation.
type Panel = "detail" | "feedback" | "disconnect" | "reconnect";

interface AppState {
  view: View;
  // The client filter: slugs this person can see, none = all. Mirrors
  // ?clients= in the address bar, so it survives moving between views,
  // reloads and shared links.
  clients: string[];
  data: ViewPayload | null;
  deadlines: DeadlinesPayload | null;
  deadlinesError: string | null;
  selectedKey: string | null; // "client_slug/bank_account_id"
  detail: DetailPayload | null;
  refreshing: boolean;
  me: Me | null;
  panel: Panel;
  connections: XeroConnectionRow[] | null;
  connectionsError: string | null;
  connectionsNotice: string | null;
  disconnect: DisconnectPanelState | null;
  reconnect: ReconnectPanelState | null;
  feedback: FeedbackPanelState;
  // What happened when the view, a refresh or a detail couldn't be
  // shown -- e.g. refused because the view couldn't be audited (decision
  // 0033). Shown, never swallowed into "Loading…" or an empty panel.
  viewError: string | null;
  detailError: string | null;
  // Per client: what went wrong on its last refresh, in the server's
  // own words (e.g. the Demo Company was reset -- decision 0025).
  refreshErrors: Map<string, string>;
  // Identity is on (the hosted console): show sign-out.
  signedIn: boolean;
  menuOpen: boolean; // phone: the rail, opened from the menu button
  meetings: MeetingsList | null;
  // One client's meetings, for its page's Meetings tab.
  clientMeetings: { slug: string; list: MeetingsList | null; error: string | null } | null;
  meetingsError: string | null;
}

const initialRoute = parseRoute(location.pathname, location.search);

const state: AppState = {
  view: initialRoute.view,
  clients: initialRoute.clients,
  data: null,
  deadlines: null,
  deadlinesError: null,
  selectedKey: null,
  detail: null,
  refreshing: false,
  me: null,
  panel: "detail",
  connections: null,
  connectionsError: null,
  connectionsNotice: null,
  disconnect: null,
  reconnect: null,
  feedback: { sending: false, error: null, sent: null },
  viewError: null,
  detailError: null,
  refreshErrors: new Map(),
  signedIn: false,
  menuOpen: false,
  meetings: null,
  clientMeetings: null,
  meetingsError: null,
};

// The page that runs itself (the meeting room, the summary review), for
// the view it was opened for.
let mounted: { key: string; node: HTMLElement; handle: Mounted } | null = null;

function unmount(): void {
  mounted?.handle.destroy();
  mounted = null;
  meetingEl.hidden = true;
  meetingEl.replaceChildren();
}

function meetingsEnabled(): boolean {
  return state.me?.meetings_enabled === true;
}

/** The Chime room (in-person meetings, guest links): off in prod even with Meetings on. */
function chimeEnabled(): boolean {
  return meetingsEnabled() && state.me?.chime_enabled === true;
}

function visibleView(view: View): View {
  return visibleViewFor(view, state.me?.can_view_onboarding ?? false, meetingsEnabled(), chimeEnabled());
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function alertBox(message: string): HTMLElement {
  const box = document.createElement("div");
  box.className = "pk-alert";
  box.setAttribute("role", "alert");
  box.textContent = message;
  return box;
}

function followUpCount(data: ViewPayload | null): number {
  if (!data) return 0;
  return data.needs_you.at_risk.length + data.needs_you.needs_review.length;
}

function filterOptions(): FilterOption[] {
  return state.me?.entities ?? [];
}

// ---- Navigation ----

/** Moves to a view: a new history entry, the filter kept in the URL. */
function navigate(view: View): void {
  const shown = visibleView(view);
  history.pushState(null, "", buildUrl(shown, state.clients));
  showView(shown);
  document.getElementById("pk-title")?.focus();
}

function showView(view: View): void {
  state.view = view;
  // A finding selected in one view has no meaning in another.
  state.selectedKey = null;
  state.detail = null;
  state.disconnect = null;
  state.reconnect = null;
  state.connectionsNotice = null;
  if (state.panel === "disconnect" || state.panel === "reconnect") state.panel = "detail";
  closeMenu(false);
  filter.close();
  if (mounted && mounted.key !== buildUrl(view, [])) unmount();
  if (view.kind === "meeting-room") {
    openRoom(view.meetingId);
    return;
  }
  appEl.hidden = false;
  render();
  if (view.kind === "deadlines") void loadDeadlines();
  if (view.kind === "meetings") void loadMeetings();
  if (view.kind === "client" && view.tab === "meetings") void loadClientMeetings(view.slug);
  // Status comes from the server, which reuses a check from the last 60 s.
  if (view.kind === "settings" && state.me?.can_manage_xero_connections) void loadConnections();
}

window.addEventListener("popstate", () => {
  // CAP-009: once the new console has started it owns back/forward; today's console must not redraw underneath it.
  if (v05Started) return;
  const route = parseRoute(location.pathname, location.search);
  const clients = sanitizeClients(route.clients, filterOptions());
  const filterChanged = clients.join(",") !== state.clients.join(",");
  state.clients = clients;
  showView(visibleView(route.view));
  if (filterChanged) void reloadForFilter();
});

// ---- Client filter ----

const filter = createClientFilter((selected) => {
  state.clients = selected;
  // Same history entry: changing the filter isn't a new page.
  history.replaceState(null, "", buildUrl(state.view, state.clients));
  void reloadForFilter();
});

/** Asks the server again for exactly the clients now shown, so every
 * data_viewed event is for an entity actually on screen. */
async function reloadForFilter(): Promise<void> {
  state.selectedKey = null;
  state.detail = null;
  render();
  try {
    state.data = await fetchView(state.clients);
    state.viewError = null;
  } catch (err) {
    if (handleAuthError(err)) return;
    state.viewError = messageOf(err, "Preekie couldn't load your clients. Reload the page to try again.");
  }
  render();
  if (state.view.kind === "deadlines") void loadDeadlines();
  if (state.view.kind === "meetings") void loadMeetings();
}

// ---- Meetings (only where meetingsEnabled) ----

async function loadMeetings(): Promise<void> {
  state.meetingsError = null;
  render();
  try {
    state.meetings = await fetchMeetings(state.clients, true);
  } catch (err) {
    if (handleAuthError(err)) return;
    state.meetingsError = messageOf(err, "Preekie couldn't load your meetings. Try again in a few minutes.");
  }
  render();
}

async function loadClientMeetings(slug: string): Promise<void> {
  state.clientMeetings = { slug, list: null, error: null };
  render();
  try {
    const list = await fetchMeetings([slug], true);
    if (state.clientMeetings?.slug === slug) state.clientMeetings.list = list;
  } catch (err) {
    if (handleAuthError(err)) return;
    if (state.clientMeetings?.slug === slug)
      state.clientMeetings.error = messageOf(err, "Preekie couldn't load this client's meetings. Try again in a few minutes.");
  }
  render();
}

/** R3: the recorder's start screen runs itself (media, meters, consent). */
function recordNode(client: string | null): HTMLElement {
  // A recording under way (or still uploading) owns this page until it's done; also after signing in again (#303).
  if (live && (live.recording() || mounted?.key === "live" || (liveRecordingId !== null && live.busyWith(liveRecordingId)))) {
    if (mounted?.key === "live") return mounted.node;
    unmount();
    const node = document.createElement("div");
    live.attach(node);
    mounted = { key: "live", node, handle: { destroy: () => undefined } };
    return node;
  }
  const key = buildUrl({ kind: "record", client }, []);
  if (mounted?.key === key) return mounted.node;
  unmount();
  const node = document.createElement("div");
  const handle = mountRecorderStart({
    root: node,
    clients: filterOptions(),
    clientSlug: client,
    recordingAvailable: true,
    timeZone: viewerTimeZone(),
    recordingLimitMinutes: state.me?.recording_limits?.limit_minutes ?? null,
    // #303: the session first, before the sound check and consent (any request needing a signed-in session will do).
    checkSession: fetchMe,
    onSignIn: signInToRecord,
    onStarted: (started, streams, startState) => {
      live?.shutdown();
      liveRecordingId = started.recording_id;
      live = startLiveRecording({
        root: node,
        streams,
        started,
        state: startState,
        clientName: filterOptions().find((c) => c.client_slug === startState.clientSlug)?.client_name ?? startState.clientSlug,
        timeZone: viewerTimeZone(),
        onOpenMeeting: (meetingId) => navigate({ kind: "meeting-summary", meetingId }),
        onRecordAgain: () => {
          // The finished recording is done uploading; the next one is a new meeting with its own consent, for the
          // same client (#303: the start screen checks the session first, and Sign in comes back here).
          live?.shutdown();
          live = null;
          liveRecordingId = null;
          unmount();
          showRecord(startState.clientSlug || null);
        },
        onSignIn: () => signInToRecord(startState.clientSlug || null),
        onSettled: () => void refreshUnsent(),
      });
      mounted = { key: "live", node, handle: { destroy: () => undefined } };
    },
  });
  mounted = { key, node, handle };
  return node;
}

// ---- The device recorder: the live recording outlives page changes ----

let live: LiveRecording | null = null;
let liveRecordingId: string | null = null;
const unsent = { recordings: [] as RecoverableRecording[], uploading: new Set<string>(), notice: null as string | null };

/** The live recording's own audio shows on its screen, not in the banner,
 * for as long as its engine is still working on it (#223's busyWith). */
async function refreshUnsent(): Promise<void> {
  if (!meetingsEnabled()) return;
  const busy = live && liveRecordingId && live.busyWith(liveRecordingId) ? liveRecordingId : null;
  unsent.recordings = await loadUnsent(busy);
  render();
}

function unsentBannerNode(): HTMLElement | null {
  if (!meetingsEnabled()) return null;
  const onRecordPage = state.view.kind === "record";
  const liveElsewhere = !!live && live.recording() && !onRecordPage;
  if (!liveElsewhere && unsent.recordings.length === 0) return null;
  const node = document.createElement("div");
  renderUnsentBanners(node, {
    recordings: unsent.recordings,
    uploading: unsent.uploading,
    timeZone: viewerTimeZone(),
    now: Date.now(),
    liveRecording: liveElsewhere,
    notice: unsent.notice,
    onBackToRecording: () => navigate({ kind: "record", client: null }),
    onUpload: (id) => {
      if (live && live.busyWith(id)) return; // never a second resume for the live engine's recording
      unsent.uploading.add(id);
      unsent.notice = null;
      render();
      void uploadUnsent(id).then((notice) => {
        unsent.notice = notice;
        unsent.uploading.delete(id);
        void refreshUnsent();
      });
    },
    onDiscard: (id, keepUploaded) => {
      unsent.notice = null;
      void discardUnsent(id, keepUploaded)
        .catch((err: unknown) => {
          // CAP-008 #266: say so, rather than leave the banner looking as if nothing happened.
          if (isSignedOut(err)) unsent.notice = SIGNED_OUT.message; // #303
          else if ((err as { status?: number } | null)?.status !== 409) unsent.notice = DISCARD_FAILED;
        })
        .finally(() => void refreshUnsent());
    },
  });
  return node;
}

function newMeeting(mode: "online" | "in_person"): void {
  openNewMeetingDialog({
    mode,
    clients: filterOptions(),
    selectedClients: state.clients,
    timeZone: viewerTimeZone(),
    create: createMeeting,
    onCreated: () => void loadMeetings(),
    onStart: (meetingId) => navigate({ kind: "meeting-room", meetingId }),
  });
}

/** The room takes the whole screen; leaving goes back to Meetings. */
function openRoom(meetingId: string): void {
  const key = buildUrl({ kind: "meeting-room", meetingId }, []);
  if (mounted?.key === key) return;
  unmount();
  appEl.hidden = true;
  meetingEl.hidden = false;
  document.title = "Meeting · Preekie";
  const handle = mountRoom({
    root: meetingEl,
    meetingId,
    videoAndShare: VIDEO_AND_SHARE,
    timeZone: viewerTimeZone(),
    onLeave: () => navigate({ kind: "meetings" }),
  });
  mounted = { key, node: meetingEl, handle };
}

function summaryNode(meetingId: string): HTMLElement {
  const key = buildUrl({ kind: "meeting-summary", meetingId }, []);
  if (mounted?.key === key) return mounted.node;
  unmount();
  const node = document.createElement("div");
  const handle = mountSummary({
    root: node,
    meetingId,
    timeZone: viewerTimeZone(),
    canReviewIfAdmin: state.me?.can_view_onboarding ?? false,
    onNavigate: navigate,
  });
  mounted = { key, node, handle };
  return node;
}

// ---- Follow-up ----

async function selectDetail(clientSlug: string, bankAccountId: string): Promise<void> {
  state.selectedKey = `${clientSlug}/${bankAccountId}`;
  state.panel = "detail";
  render();
  state.detailError = null;
  try {
    state.detail = await fetchDetail(clientSlug, bankAccountId);
  } catch (err) {
    state.detail = null;
    if (handleAuthError(err)) return;
    state.detailError = messageOf(err, "Preekie couldn't load this detail. Try again in a few minutes.");
  }
  render();
  // On a phone the detail sits below the list: bring it into view.
  if (window.matchMedia("(max-width: 760px)").matches) detailEl.scrollIntoView({ block: "start" });
}

function onSelectFinding(item: FindingItem): void {
  void selectDetail(item.client_slug, item.bank_account_id);
}

async function refreshAll(): Promise<void> {
  if (!state.data || state.refreshing) return;
  state.refreshing = true;
  state.viewError = null;
  render();
  // One POST per client shown, one at a time -- exactly one Xero call per
  // entity per refresh, never more than the user asked for.
  for (const row of state.data.clients) {
    try {
      state.data = await refreshClient(row.client_slug, state.clients);
      state.refreshErrors.delete(row.client_slug);
    } catch (err) {
      if (handleAuthError(err)) return;
      if (err instanceof ApiError && err.code === "reconnect_required") {
        // The server already dropped this client's old results and sent
        // the view without them -- show that view, not the stale one.
        state.data = err.body as unknown as ViewPayload;
        state.selectedKey = null;
        state.detail = null;
      }
      state.refreshErrors.set(
        row.client_slug,
        err instanceof ApiError ? err.message : "Preekie couldn't be reached. Check your connection and try again."
      );
    }
  }
  state.refreshing = false;
  render();
}

// ---- Deadlines ----

async function loadDeadlines(): Promise<void> {
  state.deadlines = null;
  state.deadlinesError = null;
  render();
  try {
    state.deadlines = await fetchDeadlines(state.clients);
  } catch (err) {
    if (handleAuthError(err)) return;
    state.deadlinesError = messageOf(err, "Preekie couldn't load your deadlines. Try again in a few minutes.");
  }
  render();
}

// ---- Settings: Xero connections ----

/** `fresh` = the "Check now" button: skip the server's 60-second status
 * cache. Arriving on Settings, and reloading after a disconnect, don't. */
async function loadConnections(fresh = false): Promise<void> {
  state.connections = null;
  state.connectionsError = null;
  render();
  try {
    state.connections = (await fetchXeroConnections(fresh)).connections;
  } catch (err) {
    state.connectionsError = messageOf(err, "Preekie couldn't load your Xero connections. Try again in a few minutes.");
  }
  render();
}

function onDisconnect(row: XeroConnectionRow): void {
  state.disconnect = { row, pending: false, error: null };
  state.connectionsNotice = null;
  state.panel = "disconnect";
  render();
  detailEl.querySelector<HTMLElement>("h3")?.focus();
}

async function confirmDisconnect(): Promise<void> {
  const current = state.disconnect;
  if (!current || current.pending || !current.row.connection_id) return;
  state.disconnect = { ...current, pending: true, error: null };
  render();
  try {
    const result = await disconnectXeroConnection(current.row.client_slug, current.row.connection_id);
    state.disconnect = null;
    state.panel = "detail";
    state.connectionsNotice = result.message;
    await loadConnections();
  } catch (err) {
    // Stay on the confirmation, with the specific reason -- never a
    // silent no-op (docs/closed-beta-spec.md s.4).
    state.disconnect = {
      ...current,
      pending: false,
      error: messageOf(err, "Preekie couldn't disconnect this organisation, so it's still connected. Try again in a few minutes."),
    };
    render();
  }
}

function onReconnect(row: XeroConnectionRow): void {
  state.reconnect = { row, pending: false, error: null, link: null, copied: false };
  state.connectionsNotice = null;
  state.panel = "reconnect";
  render();
  detailEl.querySelector<HTMLElement>("h3")?.focus();
}

async function getReconnectLink(): Promise<void> {
  const current = state.reconnect;
  if (!current || current.pending) return;
  state.reconnect = { ...current, pending: true, error: null };
  render();
  try {
    const link = await startXeroReconnect(current.row.client_slug);
    state.reconnect = { ...current, pending: false, link: { url: link.authorization_url, expiresAt: link.expires_at } };
  } catch (err) {
    state.reconnect = {
      ...current,
      pending: false,
      error: messageOf(err, "Preekie couldn't start reconnecting this organisation. Nothing changed. Try again in a few minutes."),
    };
  }
  render();
}

function copyReconnectLink(): void {
  const current = state.reconnect;
  if (!current?.link) return;
  void navigator.clipboard.writeText(current.link.url).then(
    () => {
      if (state.reconnect) state.reconnect = { ...state.reconnect, copied: true };
      render();
    },
    () => {
      if (state.reconnect) state.reconnect = { ...state.reconnect, error: "Couldn't copy the link. Open Xero instead, or try again." };
      render();
    }
  );
}

function closeReconnect(): void {
  state.reconnect = null;
  state.panel = "detail";
  // Fresh from Xero: a reconnect finished in another tab shows straight away.
  void loadConnections(true);
}

function cancelDisconnect(): void {
  state.disconnect = null;
  state.panel = "detail";
  render();
}

// ---- Feedback ----

const feedbackPanel = createFeedbackPanel(
  () => void submitFeedback(),
  () => closeFeedback()
);

function openFeedback(prefill?: string): void {
  state.panel = "feedback";
  state.feedback = { sending: false, error: null, sent: null };
  closeMenu(false);
  render();
  if (prefill) feedbackPanel.prefill(prefill);
  else feedbackPanel.focusFirst();
}

function toggleFeedback(): void {
  if (state.panel === "feedback") closeFeedback();
  else openFeedback();
}

function closeFeedback(): void {
  state.panel = "detail";
  render();
}

async function submitFeedback(): Promise<void> {
  if (state.feedback.sending) return;
  state.feedback = { sending: true, error: null, sent: null };
  render();
  try {
    const result = await sendFeedback({ screen: viewKey(state.view), ...feedbackPanel.values() });
    feedbackPanel.clear();
    state.feedback = { sending: false, error: null, sent: result.message };
  } catch (err) {
    // The typed text stays in the form -- nothing is lost on failure.
    state.feedback = {
      sending: false,
      error: messageOf(err, "Your feedback wasn't sent. Your text is still here. Try again in a few minutes."),
      sent: null,
    };
  }
  render();
}

// ---- Phone menu ----

function toggleMenu(): void {
  if (state.menuOpen) closeMenu(true);
  else openMenu();
}

function openMenu(): void {
  state.menuOpen = true;
  render();
  railEl.querySelector<HTMLElement>("a, button")?.focus();
}

function closeMenu(returnFocus: boolean): void {
  if (!state.menuOpen) return;
  state.menuOpen = false;
  render();
  if (returnFocus) contentEl.querySelector<HTMLElement>(".pk-menu-button")?.focus();
}

backdropEl.addEventListener("click", () => closeMenu(true));
document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape" && state.menuOpen) closeMenu(true);
});

// ---- Rendering ----

function renderRefreshErrors(): HTMLElement | null {
  if (state.refreshErrors.size === 0 || !state.data) return null;
  const box = document.createElement("div");
  box.className = "pk-alert";
  box.setAttribute("role", "alert");
  for (const [slug, message] of state.refreshErrors) {
    const name = filterOptions().find((c) => c.client_slug === slug)?.client_name ?? slug;
    const line = document.createElement("p");
    const strong = document.createElement("strong");
    strong.textContent = `${name}: `;
    line.append(strong, message);
    box.append(line);
  }
  return box;
}

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function lastCheckedText(data: ViewPayload | null): string {
  if (!data) return "";
  // The OLDEST refresh across the clients shown, so the claim is never
  // fresher than the stalest data on the page.
  const times = data.clients.map((c) => c.refreshed_at).filter((t): t is string => t !== null);
  if (times.length === 0) return "Not yet checked with Xero";
  const oldest = times.reduce((a, b) => (Date.parse(a) <= Date.parse(b) ? a : b));
  const when = new Date(oldest);
  const time = when.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  const sameDay = when.toDateString() === new Date().toDateString();
  // "27 Sep", per the brand's tight-UI date style -- not the en-AU
  // locale's "27 Sept".
  const day = `${when.getDate()} ${SHORT_MONTHS[when.getMonth()]}`;
  return `Last checked with Xero: ${sameDay ? time : `${day}, ${time}`}`;
}

function loading(text = "Loading…"): HTMLElement {
  const box = document.createElement("div");
  box.className = "pk-empty";
  box.textContent = text;
  return box;
}

function renderBody(): HTMLElement {
  const body = document.createElement("div");
  const view = state.view;
  if (view.kind === "settings") {
    let xero: HTMLElement | null = null;
    if (state.me?.can_manage_xero_connections) {
      xero = document.createElement("div");
      renderConnections(
        xero,
        {
          rows: state.connections,
          loadError: state.connectionsError,
          notice: state.connectionsNotice,
          selectedSlug:
            state.panel === "disconnect"
              ? state.disconnect?.row.client_slug ?? null
              : state.panel === "reconnect"
                ? state.reconnect?.row.client_slug ?? null
                : null,
        },
        onDisconnect,
        () => void loadConnections(true),
        onReconnect
      );
    }
    renderSettings(body, { xeroConnections: xero, email: state.me?.email ?? null });
  } else if (view.kind === "agent") {
    renderSoonPage(body, view.agent, () => openFeedback(view.agent.name));
  } else if (view.kind === "onboarding") {
    renderSoonPage(body, ONBOARDING, () => openFeedback(ONBOARDING.name));
  } else if (view.kind === "not-found") {
    renderNotFound(body, () => navigate({ kind: "follow-up" }));
  } else if (view.kind === "meetings") {
    renderMeetingsPage(body, {
      list: state.meetings,
      error: state.meetingsError,
      timeZone: viewerTimeZone(),
      clients: state.clients,
      onRecord: () => navigate({ kind: "record", client: state.clients.length === 1 ? state.clients[0]! : null }),
      onStartInPerson: chimeEnabled() ? () => newMeeting("in_person") : undefined,
      onNavigate: navigate,
    });
  } else if (view.kind === "record") {
    body.append(recordNode(view.client));
  } else if (view.kind === "clients") {
    renderClientsPage(body, { entities: filterOptions(), rows: state.data?.clients ?? [], onNavigate: navigate });
  } else if (view.kind === "client") {
    const entity = filterOptions().find((e) => e.client_slug === view.slug) ?? null;
    const cm = state.clientMeetings?.slug === view.slug ? state.clientMeetings : null;
    renderClientPage(body, {
      slug: view.slug,
      name: entity?.client_name ?? null,
      row: state.data?.clients.find((r) => r.client_slug === view.slug) ?? null,
      tab: view.tab,
      meetings: cm?.list ?? null,
      meetingsError: cm?.error ?? null,
      timeZone: viewerTimeZone(),
      canRecord: meetingsEnabled(),
      onNavigate: navigate,
      onRecord: (slug) => navigate({ kind: "record", client: slug }),
    });
  } else if (view.kind === "meeting-summary") {
    body.append(summaryNode(view.meetingId));
  } else if (view.kind === "meeting-room" || view.kind === "guest") {
    // Full screen (meetingEl), never inside the console.
  } else if (view.kind === "deadlines") {
    if (state.deadlinesError) body.append(alertBox(state.deadlinesError));
    else if (!state.deadlines) body.append(loading());
    else renderDeadlines(body, state.deadlines);
  } else if (!state.data) {
    body.append(state.viewError ? alertBox(state.viewError) : loading());
  } else {
    renderFollowUp(body, state.data.needs_you, state.selectedKey, onSelectFinding);
  }
  return body;
}

function render(): void {
  const view = state.view;
  const count = followUpCount(state.data);
  const rail: RailOptions = {
    active: view,
    clients: state.clients,
    followUpCount: count,
    showOnboarding: state.me?.can_view_onboarding ?? false,
    showMeetings: meetingsEnabled(),
    showFeedback: state.me?.can_send_feedback ?? false,
    feedbackOpen: state.panel === "feedback",
    onNavigate: navigate,
    onFeedback: toggleFeedback,
    showSignOut: state.signedIn,
    signedInEmail: state.signedIn ? state.me?.email ?? null : null,
    onSignOut,
  };
  renderRail(railNavEl, rail);
  renderRailFooter(railFooterEl, rail);
  document.body.classList.toggle("pk-menu-open", state.menuOpen);
  backdropEl.hidden = !state.menuOpen;
  document.title = `${pageTitle(view)} · Preekie`;

  filter.update(filterOptions(), state.clients);
  contentEl.replaceChildren();
  contentEl.append(
    renderTopBar({
      title: pageTitle(view),
      followUpCount: view.kind === "follow-up" ? count : null,
      menuOpen: state.menuOpen,
      onMenu: toggleMenu,
      refresh:
        view.kind === "follow-up" && state.data
          ? { lastChecked: lastCheckedText(state.data), refreshing: state.refreshing, onRefresh: () => void refreshAll() }
          : null,
      filter: filter.root,
    })
  );

  const dataViews = view.kind !== "settings" && view.kind !== "not-found" && !isMeetingsView(view);
  if (dataViews && state.data && state.data.clients.length === 1) {
    contentEl.append(renderSummaryStrip(state.data.clients[0]!));
  }
  const banner = unsentBannerNode();
  if (banner) contentEl.append(banner);
  if (state.data && state.viewError && view.kind === "follow-up") contentEl.append(alertBox(state.viewError));
  if (view.kind === "follow-up") {
    const errors = renderRefreshErrors();
    if (errors) contentEl.append(errors);
  }
  contentEl.append(renderBody());

  // The side panel: finding detail on Follow-up; feedback or a
  // disconnect confirmation on any view. Otherwise the page is one column.
  const showPanel = view.kind === "follow-up" || state.panel !== "detail";
  detailEl.hidden = !showPanel;
  mainEl.classList.toggle("pk-main-single", !showPanel);
  if (state.panel === "feedback") {
    if (detailEl.firstChild !== feedbackPanel.root) detailEl.replaceChildren(feedbackPanel.root);
    feedbackPanel.update(state.feedback);
  } else if (state.panel === "disconnect" && state.disconnect) {
    renderDisconnectPanel(detailEl, state.disconnect, () => void confirmDisconnect(), cancelDisconnect);
  } else if (state.panel === "reconnect" && state.reconnect) {
    renderReconnectPanel(detailEl, state.reconnect, () => void getReconnectLink(), copyReconnectLink, closeReconnect);
  } else {
    renderDetail(detailEl, state.detail);
    if (state.detailError) detailEl.replaceChildren(alertBox(state.detailError));
  }
}

function onSignOut(): void {
  // Session D: say what happens to a recording, or audio still waiting, first.
  const minutes = (live?.unsentMinutes() ?? 0) + unsent.recordings.reduce((n, r) => n + r.unsent.minutes, 0);
  const copy = meetingsEnabled() ? signOutCopy(live?.recording() ?? false, minutes) : null;
  if (copy) {
    confirmDialog({
      question: copy.question,
      confirm: copy.confirm,
      cancel: copy.cancel,
      onConfirm: () => {
        const stopping = live?.recording() ? live.stopForSignOut() : Promise.resolve();
        void stopping.catch(() => undefined).then(() => {
          live?.shutdown();
          live = null;
          liveRecordingId = null;
          doSignOut();
        });
      },
    });
    return;
  }
  doSignOut();
}

function doSignOut(): void {
  void signOut()
    .catch(() => undefined) // signed out locally either way
    .then(() => showAuth("You've signed out."));
}

function resetState(): void {
  state.data = null;
  state.deadlines = null;
  state.deadlinesError = null;
  state.selectedKey = null;
  state.detail = null;
  state.refreshing = false;
  state.refreshErrors.clear();
  state.viewError = null;
  state.detailError = null;
  state.me = null;
  state.panel = "detail";
  state.connections = null;
  state.connectionsError = null;
  state.connectionsNotice = null;
  state.disconnect = null;
  state.reconnect = null;
  state.feedback = { sending: false, error: null, sent: null };
  state.menuOpen = false;
  state.meetings = null;
  state.meetingsError = null;
}

function showAuth(notice?: string): void {
  unmount();
  resetState();
  forgetCsrfToken();
  state.signedIn = false;
  appEl.hidden = true;
  authEl.hidden = false;
  showSignIn(authEl, () => void init(), notice);
}

/** Record a meeting for this client, in place of the current page (only the route and the client go in the address bar,
 * as with any page; the recording's token and key never do). */
function showRecord(clientSlug: string | null): void {
  const view: View = { kind: "record", client: clientSlug };
  history.replaceState(null, "", buildUrl(view, state.clients));
  showView(view);
}

/** #303: sign in, then back to Record a meeting with this client chosen (init() reads the address bar after sign-in). */
function signInToRecord(clientSlug: string | null): void {
  history.replaceState(null, "", buildUrl({ kind: "record", client: clientSlug }, state.clients));
  showAuth(SIGNED_OUT.message);
}

/** A 401 (no or expired session) or 403 (terms not accepted) from any
 * request sends the person to the matching screen. Returns true if it
 * did, so the caller stops. */
function handleAuthError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 401) {
    showAuth(err.code === "session_expired" || err.code === "session_revoked" ? err.message : undefined);
    return true;
  }
  if (err.status === 403 && err.code === "terms_required") {
    // After a reload this response is the page's only source of the session's CSRF token (Accept and Sign out need it).
    rememberCsrfFrom(err.body);
    appEl.hidden = true;
    authEl.hidden = false;
    showTerms(
      authEl,
      String(err.body.document_version),
      String(err.body.document_hash),
      String(err.body.document_text),
      () => void init(),
      // Privacy Policy v1.2 (C9): the variant and versions come from the server; Sign out is the only other way out.
      { body: err.body as TermsRequired, onSignOut: () => signOut().then(() => showAuth("You've signed out.")) }
    );
    return true;
  }
  return false;
}

/** A guest's own link: no Preekie account, no sign-in, no console -- just
 * the lobby and then the room. The token in the link is checked by the
 * API (the server serves this page only where Meetings is on). */
function showGuest(meetingId: string, token: string): void {
  appEl.hidden = true;
  authEl.hidden = true;
  meetingEl.hidden = false;
  document.title = "Meeting · Preekie";
  mountGuest({ root: meetingEl, meetingId, token, timeZone: viewerTimeZone(), videoAndShare: VIDEO_AND_SHARE });
}

async function init(): Promise<void> {
  // The address bar decides the view and the filter, on first load and
  // after signing in (a deep link survives the sign-in screen).
  // CAP-009: an invite link (/invite#<token>) has no session yet -- its own page, before anything that needs one. The
  // token is read once from the fragment and taken out of the address bar (history, bookmarks, screen shares).
  const invite = inviteToken(location.pathname, location.hash);
  if (invite !== null) {
    if (location.hash) history.replaceState(null, "", "/invite");
    appEl.hidden = true;
    void showInviteAccept(authEl, invite);
    return;
  }
  // CAP-009: the super admin's own login and session (A, contracts s.13), never a customer account's.
  if (/^\/super-admin\/?$/.test(location.pathname)) {
    await superAdminInit();
    return;
  }
  const route = parseRoute(location.pathname, location.search);
  if (route.view.kind === "guest") {
    showGuest(route.view.meetingId, route.view.token);
    return;
  }
  let data: ViewPayload;
  try {
    // The server narrows ?clients= to this person's scope; anything else
    // in it is ignored without a trace.
    data = await fetchView(route.clients);
  } catch (err) {
    if (handleAuthError(err)) return;
    // CAP-009 (A, contracts s.15): an account awaiting payment or suspended is refused everything but /api/me; the new
    // console then shows the page that says which, instead of an error.
    if (err instanceof ApiError && err.status === 403 && (err.code === "account_pending_payment" || err.code === "account_suspended")) {
      const gated = await fetchMe().catch(() => null);
      if (gated && useV05(gated, location.search)) {
        authEl.hidden = true;
        appEl.hidden = true;
        v05Started = true;
    startV05({
          root: v05El,
          me: gated,
          timeZone: viewerTimeZone(),
          onSignOut: () =>
            void signOut()
              .catch(() => undefined)
              .then(() => location.assign("/")),
        });
        return;
      }
    }
    console.error("Failed to load the console's initial view:", err);
    // Shown, never swallowed -- e.g. a view refused because it couldn't be
    // audited (decision 0033).
    state.viewError = messageOf(err, "Preekie couldn't load your clients. Reload the page to try again.");
    appEl.hidden = false;
    authEl.hidden = true;
    render();
    return;
  }
  state.signedIn = data.signed_in === true;
  state.data = data;
  state.viewError = null;
  try {
    // Only once the view has loaded: with identity on, /api/me needs the
    // same signed-in, terms-accepted session.
    state.me = await fetchMe();
  } catch {
    // Without it, only the always-available views are shown -- every
    // route still enforces its own access either way.
    state.me = null;
  }
  if (useV05(state.me, location.search)) {
    // The new console (test only until the release): the server switch, never the browser, decides.
    authEl.hidden = true;
    authEl.replaceChildren();
    appEl.hidden = true;
    v05Started = true;
    startV05({
      root: v05El,
      me: state.me!,
      timeZone: viewerTimeZone(),
      // CAP-009.H11a: the recorder's banner and sign-out check, where Meetings is on.
      onReady: (go) => {
        if (state.me?.meetings_enabled === true) initRecorderHost(viewerTimeZone(), go);
      },
      onSignOut: () =>
        void signOut()
          .catch(() => undefined) // signed out locally either way
          .then(() => location.assign("/")),
    });
    return;
  }
  // Keep only what this person can see, and tidy the address bar to match.
  state.clients = sanitizeClients(route.clients, filterOptions());
  const view = visibleView(route.view);
  history.replaceState(null, "", view.kind === "not-found" ? location.pathname : buildUrl(view, state.clients));
  authEl.hidden = true;
  authEl.replaceChildren();
  appEl.hidden = false;
  showView(view);
  // R8: audio waiting on this computer, shown on every page from the first open.
  void refreshUnsent();
}

/** /super-admin: the super admin's session (its own cookie) opens the console's Super admin screen; without one, its
 * own sign-in. A customer session here is simply not a super admin session. */
async function superAdminInit(): Promise<void> {
  useLoginBase("/api/admin/login");
  const signedOut = new URLSearchParams(location.search).get("signed_out") === "1";
  if (signedOut) history.replaceState(null, "", "/super-admin");
  let me: Me | null = null;
  try {
    me = await fetchMe();
  } catch {
    me = null;
  }
  if (me && (me as { is_super_admin?: boolean }).is_super_admin === true) {
    await refreshCsrfFrom("/api/admin/me"); // after a reload the token isn't in memory yet
    authEl.hidden = true;
    authEl.replaceChildren();
    appEl.hidden = true;
    v05Started = true;
    startV05({
      root: v05El,
      me: { console_v05: true, is_super_admin: true, email: me.email ?? null },
      timeZone: viewerTimeZone(),
      onSignOut: () =>
        void adminSignOut()
          .catch(() => undefined)
          .then(() => location.assign("/super-admin")),
    });
    return;
  }
  appEl.hidden = true;
  authEl.hidden = false;
  showSignIn(authEl, () => void init(), signedOut ? ADMIN_COPY.expired : undefined, true);
}

void init();
